// src/pages/AppStats.jsx
// Analytics → Apps (3.83.3, owner, Nov 4; Super Admins only): how many
// downloads, who uses which app, on which system and version. Its own records
// (lib/appUsage.js), apart from the Activity log.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import SortTh, { useSort } from '../components/SortTh';
import ShowSelect from '../components/ShowSelect';
import Pager, { usePager } from '../components/Pager';
import EmptyState from '../components/EmptyState';
import Loader from '../components/Loader';
import { API_BASE } from '../firebase/config';
import { downloadSheet } from '../utils/api';
import { formatDate } from '../utils/format';

const STATUS = { 'Up to date': ['var(--c-e0f2f1)', 'var(--t-00695c)'], 'Old version': ['var(--c-fff3e0)', 'var(--t-e65100)'], 'Version unknown': ['var(--c-eceff1)', 'var(--t-546e7a)'] };
const person = r => r.name || r.email || '—';
const client = r => r.accountName || (r.accountId ? r.accountId : 'Super Admin');

export default function AppStats() {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [app, setApp] = useState('');
  const [system, setSystem] = useState('');
  const [status, setStatus] = useState('');
  const [company, setCompany] = useState('');
  const [show, setShow] = useState('50');
  const [logShow, setLogShow] = useState('50');
  const list = useRef(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/portal/control/app-stats`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || "Couldn't load the app figures.");
        if (live) setData(j);
      } catch (e) { if (live) { setError(e.message); setData({ totals: {}, files: [], log: [], people: [], versions: [] }); } }
    })();
    return () => { live = false; };
  }, [getToken]);

  const people = useMemo(() => data?.people || [], [data]);
  const pick = key => [...new Set(people.map(key))].filter(Boolean).sort();
  const shown = useMemo(() => people.filter(p => (!app || p.appLabel === app) && (!system || p.systemLabel === system) && (!status || p.status === status) && (!company || client(p) === company)),
    [people, app, system, status, company]);
  const { sorted, sort, onSort } = useSort(shown, {
    who: person, company: client, app: p => p.appLabel, system: p => p.systemLabel, version: p => p.version || '', last: p => p.lastSeen || '', status: p => p.status,
  });
  const { pageRows, pager } = usePager(sorted, show);
  const log = useMemo(() => data?.log || [], [data]);
  const logPages = usePager(log, logShow);
  const fileLabel = id => (data?.files || []).find(f => f.id === id)?.label || id;
  const top = Math.max(1, ...(data?.versions || []).map(v => v.count));

  async function download() {
    await downloadSheet('App users', [
      { header: 'Person', get: person, width: 26 }, { header: 'Email', get: p => p.email, width: 32 }, { header: 'Client', get: client, width: 18 },
      { header: 'App', get: p => p.appLabel, width: 14 }, { header: 'System', get: p => p.systemLabel, width: 14 }, { header: 'Version', get: p => p.version || '', width: 10 },
      { header: 'Status', get: p => p.status, width: 16 }, { header: 'First used', get: p => (p.firstSeen ? formatDate(p.firstSeen) : ''), width: 22 }, { header: 'Last used', get: p => (p.lastSeen ? formatDate(p.lastSeen) : ''), width: 22 },
    ], sorted, getToken);
  }

  // 3.84.8 (owner): the card opens the people it counts
  const showOld = () => {
    setApp(''); setSystem(''); setCompany(''); setStatus('Old version');
    list.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!data) return <Loader label="Counting downloads…" />;
  const t = data.totals;
  const select = (value, set, label, all, list) => (
    <select value={value} onChange={e => set(e.target.value)} aria-label={label} style={S.input}>
      <option value="">{all}</option>{list.map(x => <option key={x} value={x}>{x}</option>)}
    </select>
  );
  return (
    <div>
      {error && <div style={S.error}>{error}</div>}
      <div style={S.tiles}>
        {[['Downloads', t.downloads, 'Every download, all time'], ['People using an app', t.people, `On ${t.devices || 0} device${t.devices === 1 ? '' : 's'}`],
          ['Sign-ins from apps', t.signIns, 'Last 7 days'], ['On an old version', t.old, 'CP Fax app, not the newest', showOld]].map(([label, value, about, open]) => {
          const inside = <><div style={S.value}>{(value || 0).toLocaleString()}</div><div style={S.label}>{label}</div><div style={S.about}>{about}</div></>;
          return open && value > 0
            ? <button key={label} type="button" className="ov-lift" onClick={open} style={S.tileBtn}>{inside}</button>
            : <div key={label} style={S.tile}>{inside}</div>;
        })}
      </div>
      <div style={S.two}>
        <section style={S.panel}>
          <h3 style={S.title}>Downloads by app</h3>
          <table style={S.table}><thead><tr><th style={S.thin}>App</th><th style={S.thin}>Version</th><th style={S.thin}>Downloads</th><th style={S.thin}>Last download</th></tr></thead>
            <tbody>{data.files.map(f => <tr key={f.id}><td style={S.cell}>{f.label}</td><td style={S.cell}>{f.version}</td><td style={{ ...S.cell, fontWeight: 700 }}>{f.count}</td><td style={S.cell}>{f.last ? formatDate(f.last) : '—'}</td></tr>)}</tbody></table>
        </section>
        <section style={S.panel}>
          <h3 style={S.title}>Versions in use</h3>
          {!data.versions.length ? <div style={S.about}>No CP Fax app has reported in yet.</div> : data.versions.map(v => (
            <div key={v.label} style={S.bar}><span style={S.barName}>{v.label}</span><span style={S.barTrack}><span style={{ ...S.barFill, width: `${(v.count / top) * 100}%` }} /></span><strong style={S.barNum}>{v.count}</strong></div>
          ))}
        </section>
      </div>

      <section ref={list} style={{ ...S.panel, scrollMarginTop: 90 }}>
        <div style={S.head}><h3 style={{ ...S.title, margin: 0 }}>Who’s using what</h3>
          <button onClick={download} disabled={!sorted.length} style={S.btn}>⬇ Download report (.xlsx)</button></div>
        <div style={S.row}>
          {select(company, setCompany, 'Client', 'All clients', pick(client))}
          {select(app, setApp, 'App', 'All apps', pick(p => p.appLabel))}
          {select(system, setSystem, 'System', 'All systems', pick(p => p.systemLabel))}
          {select(status, setStatus, 'Status', 'All statuses', pick(p => p.status))}
          <ShowSelect value={show} onChange={setShow} style={S.input} groupStyle={{ display: 'flex', alignItems: 'center', gap: 6 }} />
        </div>
        <div style={S.wrap}><table style={S.table}>
          <thead><tr style={S.thead}>
            <SortTh k="who" label="Person" sort={sort} onSort={onSort} style={S.th} /><SortTh k="company" label="Client" sort={sort} onSort={onSort} style={S.th} />
            <SortTh k="app" label="App" sort={sort} onSort={onSort} style={S.th} /><SortTh k="system" label="System" sort={sort} onSort={onSort} style={S.th} />
            <SortTh k="version" label="Version" sort={sort} onSort={onSort} style={S.th} /><SortTh k="last" label="Last used" sort={sort} onSort={onSort} style={S.th} />
            <SortTh k="status" label="Status" sort={sort} onSort={onSort} style={S.th} /></tr></thead>
          <tbody>{!pageRows.length ? <tr><td colSpan={7} style={S.empty}><EmptyState icon="📲" emptyText="Nobody has used an app yet." /></td></tr> : pageRows.map(p => {
            const [bg, color] = STATUS[p.status] || STATUS['Version unknown'];
            return <tr key={p.id}><td style={S.cell}>{person(p)}{p.name && <div style={S.about}>{p.email}</div>}</td><td style={S.cell}>{client(p)}</td><td style={S.cell}>{p.appLabel}</td><td style={S.cell}>{p.systemLabel}</td>
              <td style={S.cell}>{p.version || '—'}</td><td style={S.cell}>{p.lastSeen ? formatDate(p.lastSeen) : '—'}</td><td style={S.cell}><span style={{ ...S.badge, background: bg, color }}>{p.status}</span></td></tr>;
          })}</tbody></table></div>
        <Pager {...pager} label="devices" />
      </section>

      <section style={S.panel}>
        <div style={S.head}><h3 style={{ ...S.title, margin: 0 }}>Download log</h3><ShowSelect value={logShow} onChange={setLogShow} style={S.input} groupStyle={{ display: 'flex', alignItems: 'center', gap: 6 }} /></div>
        <div style={S.wrap}><table style={S.table}>
          <thead><tr style={S.thead}>{['When', 'Person', 'Client', 'App', 'Version'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>{!logPages.pageRows.length ? <tr><td colSpan={5} style={S.empty}><EmptyState icon="📲" emptyText="No downloads yet." /></td></tr> : logPages.pageRows.map(r => (
            <tr key={r.id}><td style={S.cell}>{formatDate(r.at)}</td><td style={S.cell}>{person(r)}</td><td style={S.cell}>{client(r)}</td><td style={S.cell}>{fileLabel(r.file)}</td><td style={S.cell}>{r.version || '—'}</td></tr>
          ))}</tbody></table></div>
        <Pager {...logPages.pager} label="downloads" />
      </section>
    </div>
  );
}

const S = {
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginBottom: 14 },
  tile: { background: 'var(--c-ffffff)', border: '1px solid var(--b-c8e6c9)', borderRadius: 12, padding: '10px 12px' },
  tileBtn: { background: 'var(--c-ffffff)', border: '1px solid var(--b-c8e6c9)', borderRadius: 12, padding: '10px 12px', font: 'inherit', textAlign: 'left', cursor: 'pointer' },
  value: { fontSize: 22, fontWeight: 800, color: 'var(--t-00695c)' },
  label: { fontSize: 11, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 2 },
  about: { fontSize: 12, color: 'var(--t-90a4ae)', marginTop: 2 },
  two: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 },
  panel: { background: 'var(--c-ffffff)', border: '1px solid var(--b-c8e6c9)', borderRadius: 12, padding: '12px 16px', marginBottom: 14, minWidth: 0 },
  title: { fontSize: 15, fontWeight: 700, color: 'var(--t-00695c)', margin: '0 0 10px' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 },
  row: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 },
  input: { padding: '7px 10px', border: '1.5px solid var(--c-cfd8dc)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)', color: 'var(--t-333333)' },
  btn: { padding: '7px 12px', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', border: '1.5px solid var(--c-b2dfdb)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  wrap: { overflow: 'auto', borderRadius: 10, border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  th: { padding: '10px 12px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
  thin: { padding: '6px 8px 6px 0', textAlign: 'left', color: 'var(--t-78909c)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' },
  cell: { padding: '8px 12px 8px 0', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'top' },
  empty: { padding: 24, textAlign: 'center' },
  badge: { padding: '3px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },
  bar: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, color: 'var(--t-37474f)' },
  barName: { width: 130, flexShrink: 0 },
  barTrack: { flex: 1, height: 10, background: 'var(--c-eceff1)', borderRadius: 5, overflow: 'hidden', minWidth: 0 },
  barFill: { display: 'block', height: '100%', background: 'var(--c-00897b)', borderRadius: 5 },
  barNum: { width: 28, textAlign: 'right' },
};
